import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { boardResponseSchema, conversationListResponseSchema } from '@shared/api';
import type { BoardRow } from '@shared/types';
import { api, qs } from '@web/lib/api';
import { keys } from '@web/lib/keys';

/** The board holds up to this many live conversations. Three staff, one screen. */
const BOARD_LIMIT = 60;

export function useBoard(): UseQueryResult<BoardRow[]> {
  return useQuery({
    queryKey: keys.board,
    queryFn: async () => {
      const response = await api.get(
        `/api/board${qs({ limit: BOARD_LIMIT })}`,
        boardResponseSchema,
      );
      return response.rows;
    },
  });
}

/**
 * The "resolved today" figure in the summary strip.
 *
 * Derived on the client from the resolved page rather than from a dedicated
 * endpoint, because no such endpoint exists yet — see the report. The
 * consequence is honest but real: on a day with more than 100 resolutions this
 * under-counts, and the number is capped rather than wrong.
 */
export function useResolvedToday(): UseQueryResult<{
  count: number;
  capped: boolean;
  rows: BoardRow[];
}> {
  return useQuery({
    queryKey: keys.resolvedToday,
    queryFn: async () => {
      const response = await api.get(
        `/api/conversations${qs({ handlingState: 'resolved', limit: 100 })}`,
        conversationListResponseSchema,
      );
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const now = Date.now();

      const rows = response.items
        .filter((item) => new Date(item.updatedAt).getTime() >= startOfDay.getTime())
        .map<BoardRow>((item) => ({
          conversationId: item.id,
          rev: item.rev,
          candidateName: item.contact.name ?? item.contact.pushName ?? item.contact.phone,
          phone: item.contact.phone,
          trade: item.trade,
          handlingState: item.handlingState,
          assignee: item.assignee,
          temperature: item.temperature,
          attentionScore: item.attentionScore,
          attentionFlags: item.attentionFlags,
          parkReason: item.parkReason,
          lastMessagePreview: item.lastMessagePreview,
          lastMessageAt: item.lastMessageAt,
          idleMs:
            item.lastMessageAt === null
              ? 0
              : Math.max(0, now - new Date(item.lastMessageAt).getTime()),
        }));

      return {
        count: rows.length,
        capped: response.nextCursor !== null && rows.length === response.items.length,
        rows,
      };
    },
    // The one figure in the console that is not event-driven: nothing broadcasts
    // "resolved", so it is refreshed on a slow timer rather than pretending to
    // be live.
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}
